import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Zap, Heart, Sparkles } from 'lucide-react';
import { useProfileStore } from '../store/profileStore';

export function WelcomePage() {
  const navigate = useNavigate();
  const { setHormoneType, setHasSelectedProfile } = useProfileStore();

  const handleSelection = (type: 'testosterone' | 'estrogen') => {
    // First reset any existing profile
    setHasSelectedProfile(false);
    
    // Set hormone type
    setHormoneType(type);
    
    // Navigate to onboarding
    navigate('/onboarding');
  };

  return (
    <div className="fixed inset-0 bg-background">
      {/* Ambient Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-purple-500/10 to-background"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_var(--tw-gradient-stops))] from-blue-500/10 via-cyan-500/5 to-transparent"></div>
        <div className="absolute inset-0 backdrop-blur-[100px]"></div>
      </div>

      <div className="relative h-full flex flex-col items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-8 max-w-sm"
        >
          {/* Welcome Message */}
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-2 text-primary">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">Welcome to the Future of Fitness</span>
              <Sparkles className="w-4 h-4" />
            </div>

            {/* App Name with Animated Gradient */}
            <div className="relative">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-primary via-purple-400 to-blue-500 bg-clip-text text-transparent">
                TODAY Move
              </h1>
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-purple-400/20 to-blue-500/20 blur-3xl animate-pulse"></div>
            </div>

            {/* Personalization Message */}
            <p className="text-base text-muted-foreground">
              Let's personalize your experience based on your hormone profile
            </p>
          </div>

          {/* Profile Selection */}
          <div className="grid grid-cols-1 gap-4 w-full">
            {/* Testosterone Profile */}
            <motion.button
              onClick={() => handleSelection('testosterone')}
              className="relative overflow-hidden rounded-xl bg-gradient-to-br from-purple-800/10 via-purple-600/10 to-indigo-500/10 p-6 transition-all duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-800/10 via-purple-600/10 to-indigo-500/10 animate-pulse" />
              <div className="absolute -inset-1 bg-gradient-to-br from-purple-800/30 to-indigo-500/30 blur-2xl animate-pulse" />
              
              <div className="relative space-y-4">
                <div className="w-12 h-12 mx-auto rounded-full bg-purple-800/20 flex items-center justify-center relative">
                  <Zap className="w-6 h-6 text-purple-400" />
                  <div className="absolute inset-0 bg-purple-400/20 blur-xl rounded-full animate-pulse"></div>
                </div>
                <div className="text-center">
                  <h2 className="text-lg font-semibold mb-1">Testosterone Profile</h2>
                  <p className="text-sm text-muted-foreground">
                    Optimized for testosterone-based hormone profiles
                  </p>
                </div>
              </div>
            </motion.button>

            {/* Estrogen Profile */}
            <motion.button
              onClick={() => handleSelection('estrogen')}
              className="relative overflow-hidden rounded-xl bg-gradient-to-br from-purple-500/10 via-amber-500/10 to-pink-500/10 p-6 transition-all duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-amber-500/10 to-pink-500/10 animate-pulse" />
              <div className="absolute -inset-1 bg-gradient-to-br from-amber-500/30 to-pink-500/30 blur-2xl animate-pulse" />
              
              <div className="relative space-y-4">
                <div className="w-12 h-12 mx-auto rounded-full bg-amber-500/20 flex items-center justify-center relative">
                  <Heart className="w-6 h-6 text-amber-500" />
                  <div className="absolute inset-0 bg-amber-500/20 blur-xl rounded-full animate-pulse"></div>
                </div>
                <div className="text-center">
                  <h2 className="text-lg font-semibold mb-1">Estrogen Profile</h2>
                  <p className="text-sm text-muted-foreground">
                    Enhanced with cycle tracking and hormone insights
                  </p>
                </div>
              </div>
            </motion.button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}